/**
 * Tiny formatting helpers used across pages. We deliberately avoid pulling in
 * a heavy date library — date-fns is already in deps but a few one-liners
 * here keep call sites sharp.
 */

export function formatDate(value?: string | Date | null, opts?: Intl.DateTimeFormatOptions) {
	if (!value) return '—';
	const d = typeof value === 'string' ? new Date(value) : value;
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		...opts,
	});
}

export function formatDateTime(value?: string | Date | null) {
	if (!value) return '—';
	const d = typeof value === 'string' ? new Date(value) : value;
	if (Number.isNaN(d.getTime())) return '—';
	return d.toLocaleString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

/**
 * "in 3 days", "5h left", "ends in 12m", "ended 2d ago".
 * Compact — designed to fit a small status cell.
 */
export function relativeTimeLeft(target?: string | Date | null) {
	if (!target) return '—';
	const t = typeof target === 'string' ? new Date(target).getTime() : target.getTime();
	if (Number.isNaN(t)) return '—';
	const diff = t - Date.now();
	const sign = diff >= 0 ? 1 : -1;
	const abs = Math.abs(diff);
	const minutes = Math.round(abs / 60_000);
	const hours = Math.round(abs / 3_600_000);
	const days = Math.round(abs / 86_400_000);

	if (sign > 0) {
		if (days >= 2) return `${days}d ${Math.round((abs % 86_400_000) / 3_600_000)}h`;
		if (hours >= 1) return `${hours}h ${Math.max(0, minutes - hours * 60)}m`;
		if (minutes >= 1) return `${minutes}m`;
		return 'Closing now';
	}
	if (days >= 2) return `${days}d ago`;
	if (hours >= 1) return `${hours}h ago`;
	if (minutes >= 1) return `${minutes}m ago`;
	return 'Just now';
}

/**
 * "Just now", "3m ago", "2h ago", "yesterday", "May 8" — for compact
 * timestamps in lists (notifications, audit feed). Always returns a value
 * suitable for inline rendering, never throws.
 */
export function relativeTimeAgo(value?: string | Date | null) {
	if (!value) return '—';
	const t = typeof value === 'string' ? new Date(value).getTime() : value.getTime();
	if (Number.isNaN(t)) return '—';
	const diffMs = Date.now() - t;
	if (diffMs < 0) return 'Just now';
	const minutes = Math.round(diffMs / 60_000);
	if (minutes < 1) return 'Just now';
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.round(diffMs / 3_600_000);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.round(diffMs / 86_400_000);
	if (days === 1) return 'Yesterday';
	if (days < 7) return `${days}d ago`;
	return formatDate(value);
}

export function formatCurrency(value?: number | null, currency = 'USD') {
	if (value == null || Number.isNaN(value)) return '—';
	return new Intl.NumberFormat(undefined, {
		style: 'currency',
		currency,
		maximumFractionDigits: 2,
	}).format(value);
}

export function isLate(target?: string | Date | null): boolean {
	if (!target) return false;
	const t = typeof target === 'string' ? new Date(target).getTime() : target.getTime();
	return !Number.isNaN(t) && t < Date.now();
}
